// The detonation look registry: one World theme look id in, one recipe out
// (R2079, `ui-map-interface.md` §6.3).
//
// One field of a World body names a detonation look: `detonation`, which is
// what a round draws where it goes off, out to the splash of its kind. The
// player picks one of ten; this module turns the id into the draw code and
// the figure row, and decides nothing else.
//
// **The arithmetic is not here.** `detonation-recipe-kit.ts` holds the
// context type, the eases and every primitive. This module re-exports the
// kit, so an importer takes one name from one place.
//
// **A look that is not built draws the AUTO look**, recipe and row, the rule
// of the hold-fire looks (R1439): a recipe file lists it in `PENDING`, and
// every id a player can pick draws something.
//
// This is a leaf (`leaves.test.ts`).

import { DETONATION_STYLES } from '../gen/balance';
import { DETONATION_LOOKS } from '../gen/presentation';
import type { DetonationRow } from '../gen/presentation';
import type { DetonationRecipe } from './detonation-recipe-kit';
import {
  PENDING as CONTAINED_PENDING, RECIPES as CONTAINED,
} from './detonation-recipes-contained';
import { PENDING as SPRAWL_PENDING, RECIPES as SPRAWL } from './detonation-recipes-sprawl';
import { lookRegistry, type Look, type RecipeFile } from './recipe-kit';

export * from './detonation-recipe-kit';

/** The World field that names a detonation look. */
export const DETONATION_FIELD = 'detonation';

/** Every look id, in cycle order. Position 0 is the AUTO look (R822). */
export const DETONATION_IDS: readonly string[] = DETONATION_STYLES;

/** The AUTO look: the id every pending look draws. */
export const DETONATION_AUTO = DETONATION_IDS[0];

/** One resolved look: the id asked for, its figures and its draw code. */
export type DetonationLook = Look<DetonationRow, DetonationRecipe>;

/** The two recipe files: the contained blasts of group A, and the sprawl
 *  of groups B and C. */
const FILES: readonly RecipeFile<DetonationRecipe>[] = [
  { recipes: CONTAINED, pending: CONTAINED_PENDING },
  { recipes: SPRAWL, pending: SPRAWL_PENDING },
];

const REGISTRY = lookRegistry<DetonationRow, DetonationRecipe>(
  DETONATION_IDS, FILES, DETONATION_LOOKS as Readonly<Record<string, DetonationRow>>,
);

/** The look of one id, or the AUTO look for an id this build does not hold. */
export const detonationLook: (id: string) => DetonationLook = REGISTRY.look;

/** Which ids each recipe file has built, and which it left pending. */
export const fileHalves = REGISTRY.fileHalves;
